/**
 * CommentsSheet — the comments and threads on one card.
 *
 * Anyone can read. Writing takes an account with a handle, so the composer shows the door
 * instead of a box when there is none. Threads are one level deep: roots newest first, replies
 * under their root in order. The server is the authority on every rule; this just talks to it.
 */
import { For, Show, createEffect, createResource, createSignal, on, onCleanup } from 'solid-js';
import { linkGate, type Account } from '../api/account';
import { deleteComment, fetchComments, postComment, threadsOf, type Comment, type Thread } from '../api/comments';
import { supabaseConfigured } from '../backend/supabase';
import type { GameMeta } from '../lib/protocol';

export interface CommentsSheetProps {
  /** The card whose comments are open, or null when closed. */
  card: GameMeta | null;
  account: Account;
  onClose: () => void;
  onNeedAccount: (view: 'create' | 'signin' | 'confirm' | 'profile') => void;
  /** The live count changed (posted or deleted), so the rail badge can follow. */
  onCount: (cardId: string, count: number) => void;
  toast: (message: string) => void;
}

function ago(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export function CommentsSheet(props: CommentsSheetProps) {
  const [list, { mutate, refetch }] = createResource(
    () => props.card?.id ?? null,
    async (id) => (id ? fetchComments(id) : []),
  );
  const [draft, setDraft] = createSignal('');
  const [replyTo, setReplyTo] = createSignal<Comment | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  let box: HTMLTextAreaElement | undefined;

  const threads = (): Thread[] => threadsOf(list() ?? []);
  const gate = () => linkGate(props.account);

  createEffect(
    on(
      () => props.card?.id,
      () => {
        setDraft('');
        setReplyTo(null);
        setError(null);
      },
    ),
  );
  createEffect(() => {
    if (!props.card) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', onKey);
    onCleanup(() => window.removeEventListener('keydown', onKey));
  });

  const submit = async (e: Event) => {
    e.preventDefault();
    const card = props.card;
    if (!card || busy()) return;
    setBusy(true);
    setError(null);
    const r = await postComment(card.id, draft(), replyTo()?.id ?? null);
    setBusy(false);
    if (!r.ok) {
      if (r.code === 'account_required') props.onNeedAccount('create');
      else if (r.code === 'handle_required') props.onNeedAccount('profile');
      else setError(r.reason);
      return;
    }
    mutate((cur) => [...(cur ?? []), r.comment]);
    props.onCount(card.id, (list() ?? []).length);
    setDraft('');
    setReplyTo(null);
  };

  const remove = async (c: Comment) => {
    const card = props.card;
    if (!card) return;
    if (await deleteComment(c.id)) {
      mutate((cur) => (cur ?? []).filter((x) => x.id !== c.id));
      props.onCount(card.id, (list() ?? []).length);
      props.toast('Comment removed');
    }
  };

  const Row = (p: { c: Comment; reply?: boolean }) => (
    <li class="comment" classList={{ reply: !!p.reply }} data-testid="comment">
      <div class="who">
        <b>@{p.c.handle}</b>
        <span class="when">{ago(p.c.created_at)}</span>
        <Show when={p.c.mine}>
          <button type="button" class="linkish quiet" onClick={() => void remove(p.c)} aria-label="Remove your comment">
            remove
          </button>
        </Show>
      </div>
      <p>{p.c.body}</p>
      <Show when={!p.reply}>
        <button
          type="button"
          class="linkish quiet"
          onClick={() => {
            setReplyTo(p.c);
            queueMicrotask(() => box?.focus());
          }}
        >
          reply
        </button>
      </Show>
    </li>
  );

  return (
    <Show when={props.card}>
      {(card) => (
        <>
          <div class="sheet-veil" onClick={() => props.onClose()} aria-hidden="true" />
          <div class="link-sheet comments-sheet" data-ui data-testid="comments-sheet" role="dialog" aria-modal="true" aria-label={`Comments on ${card().title}`}>
            <h2>
              Comments <span class="count" data-testid="comments-total">{(list() ?? []).length}</span>
            </h2>
            <p class="hint">on {card().title}</p>
            <div class="comments-scroll">
              <Show when={!list.loading} fallback={<p class="hint">Loading…</p>}>
                <Show when={threads().length} fallback={<p class="hint" data-testid="comments-empty">{supabaseConfigured ? 'Nothing yet. Say the first thing.' : 'This build has no backend, so there are no comments to show.'}</p>}>
                  <ul class="comments">
                    <For each={threads()}>
                      {(t) => (
                        <>
                          <Row c={t.root} />
                          <For each={t.replies}>{(r) => <Row c={r} reply />}</For>
                        </>
                      )}
                    </For>
                  </ul>
                </Show>
              </Show>
            </div>

            <Show
              when={gate() === 'none'}
              fallback={
                <div class="row door">
                  <span class="hint">
                    {gate() === 'confirm' ? 'Confirm your email to comment.' : gate() === 'handle' ? 'Pick a handle to comment.' : 'Anyone can read. Commenting takes an account.'}
                  </span>
                  <button type="button" class="btn primary" data-testid="comments-door" onClick={() => props.onNeedAccount(gate() === 'confirm' ? 'confirm' : gate() === 'handle' ? 'profile' : 'create')}>
                    {gate() === 'confirm' ? 'I opened it' : gate() === 'handle' ? 'Pick a handle' : 'Create account'}
                  </button>
                </div>
              }
            >
              <form class="composer" onSubmit={(e) => void submit(e)}>
                <Show when={replyTo()}>
                  {(r) => (
                    <p class="hint replying">
                      Replying to <b>@{r().handle}</b> ·{' '}
                      <button type="button" class="linkish" onClick={() => setReplyTo(null)}>
                        cancel
                      </button>
                    </p>
                  )}
                </Show>
                <textarea
                  ref={box}
                  rows="2"
                  maxlength="500"
                  placeholder={replyTo() ? 'Your reply' : 'Say something'}
                  value={draft()}
                  onInput={(e) => setDraft(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit(e);
                  }}
                  data-testid="comment-box"
                />
                <Show when={error()}>
                  <p class="error" role="alert" data-testid="comment-error">{error()}</p>
                </Show>
                <div class="row">
                  <span class="hint counter">{draft().trim().length}/500</span>
                  <button type="submit" class="btn primary" disabled={busy() || !draft().trim()} data-testid="comment-submit">
                    {busy() ? 'Posting…' : replyTo() ? 'Reply' : 'Post'}
                  </button>
                </div>
              </form>
            </Show>
            <button type="button" class="linkish quiet refresh" onClick={() => void refetch()} aria-label="Refresh comments">
              refresh
            </button>
          </div>
        </>
      )}
    </Show>
  );
}
